import { useState, useEffect, useCallback } from 'react';
import { Check, X, Settings, Mail } from 'lucide-react';

interface PreferencesPageProps {
  token: string;
  onClose: () => void;
}

interface SubscriberData {
  name: string;
  email: string;
  city: string;
  country: string;
  is_active: boolean;
  notify_20min: boolean;
  notify_10min: boolean;
  notify_exact: boolean;
}

export function PreferencesPage({ token, onClose }: PreferencesPageProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [subscriber, setSubscriber] = useState<SubscriberData | null>(null);
  const [preferences, setPreferences] = useState({
    notify_20min: true,
    notify_10min: true,
    notify_exact: true,
  });

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-preferences?token=${token}`
      );

      if (!response.ok) {
        throw new Error('Failed to load preferences');
      }

      const data = await response.json();
      setSubscriber(data);
      setPreferences({
        notify_20min: data.notify_20min,
        notify_10min: data.notify_10min,
        notify_exact: data.notify_exact,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-preferences?token=${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(preferences),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!confirm('Are you sure you want to unsubscribe from all prayer time notifications?')) {
      return;
    }

    window.location.href = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-preferences?action=unsubscribe&token=${token}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 flex items-center justify-center p-4">
      {loading && (
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your preferences...</p>
        </div>
      )}

      {!loading && (!subscriber || !subscriber.is_active) && (
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Not Found</h2>
          <p className="text-gray-600 mb-6">This subscription link is invalid or has been deactivated.</p>
          <button
            onClick={onClose}
            className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
          >
            Return to App
          </button>
        </div>
      )}

      {!loading && subscriber && subscriber.is_active && (
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Notification Preferences</h1>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="mt-2 text-emerald-100">Manage your prayer time notifications</p>
        </div>

        <div className="p-8">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-emerald-900">{subscriber.name}</span>
            </div>
            <p className="text-sm text-emerald-700">{subscriber.email}</p>
            <p className="text-sm text-emerald-600 mt-1">
              📍 {subscriber.city}, {subscriber.country}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>Preferences saved successfully!</span>
            </div>
          )}

          <div className="space-y-4 mb-8">
            <h3 className="font-semibold text-gray-900 text-lg mb-4">Choose which notifications to receive:</h3>

            <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.notify_20min}
                onChange={(e) => setPreferences({ ...preferences, notify_20min: e.target.checked })}
                className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">20-Minute Advance Notice</div>
                <div className="text-sm text-gray-600">Receive a reminder 20 minutes before each prayer time</div>
              </div>
            </label>

            <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.notify_10min}
                onChange={(e) => setPreferences({ ...preferences, notify_10min: e.target.checked })}
                className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">10-Minute Advance Notice</div>
                <div className="text-sm text-gray-600">Receive a reminder 10 minutes before each prayer time</div>
              </div>
            </label>

            <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.notify_exact}
                onChange={(e) => setPreferences({ ...preferences, notify_exact: e.target.checked })}
                className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Exact Prayer Time</div>
                <div className="text-sm text-gray-600">Receive a notification at the exact prayer time</div>
              </div>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Save Preferences
                </>
              )}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleUnsubscribe}
              className="w-full text-red-600 hover:text-red-700 font-medium py-2 transition-colors text-sm"
            >
              Unsubscribe from all notifications
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
